/**
 * Browser console logging for the Owlbert web UI.
 *
 * **Where logs appear:** only in the **browser** DevTools console (F12 → Console), not in the terminal
 * where `bun run dev:ui` / Vite runs. If a level is hidden, open the Console filter and enable **All levels**
 * (Chrome may hide `console.info` / `console.debug` by default — this module uses `console.log` for `info`
 * so messages show with typical defaults).
 *
 * **Verbose** (`debug` / `info` / `warn`): enabled when `import.meta.env.DEV`, or when
 * `localStorage.setItem('owlbert_web_log', '1')` (then reload), or `?owlbert_web_log=1` in the URL.
 *
 * **Errors**: always emitted with `[OwlbertWeb]` so production issues are still visible in DevTools.
 */

export type WebLogLevel = 'debug' | 'info' | 'warn' | 'error'

const PREFIX = '[OwlbertWeb]'
export const WEB_LOG_STORAGE_KEY = 'owlbert_web_log'
export const WEB_LOG_QUERY_KEY = 'owlbert_web_log'

export function isWebVerboseLogEnabled(): boolean {
  if (import.meta.env.DEV) return true
  try {
    if (typeof window === 'undefined') return false
    if (window.localStorage.getItem(WEB_LOG_STORAGE_KEY) === '1') return true
    if (new URLSearchParams(window.location.search).get(WEB_LOG_QUERY_KEY) === '1') return true
  } catch {
    /* ignore */
  }
  return false
}

function write(level: WebLogLevel, scope: string, message: string, detail?: unknown) {
  const line = `${PREFIX} [${scope}] ${message}`
  const args = detail === undefined ? [line] : [line, detail]
  switch (level) {
    case 'debug':
      // eslint-disable-next-line no-console
      console.log(...args)
      break
    case 'info':
      // Use console.log so DevTools default levels show Owlbert lines (info/debug are often filtered).
      // eslint-disable-next-line no-console
      console.log(...args)
      break
    case 'warn':
      // eslint-disable-next-line no-console
      console.warn(...args)
      break
    case 'error':
      // eslint-disable-next-line no-console
      console.error(...args)
      break
  }
}

/** Verbose logs when dev or opt-in; errors always print. */
export function webLog(level: WebLogLevel, scope: string, message: string, detail?: unknown) {
  if (level === 'error') {
    write(level, scope, message, detail)
    return
  }
  if (!isWebVerboseLogEnabled()) return
  write(level, scope, message, detail)
}

/** Like `webLog` but warnings are always printed (high-signal diagnostics). */
export function webLogAlwaysWarn(scope: string, message: string, detail?: unknown) {
  // eslint-disable-next-line no-console
  console.warn(`${PREFIX} [${scope}] ${message}`, detail !== undefined ? detail : '')
}

/** One-shot banner so `vite dev` users know where to look (browser console, not the Vite terminal). */
export function logWebUiDevBanner(): void {
  if (!import.meta.env.DEV) return
  // eslint-disable-next-line no-console
  console.log(
    `${PREFIX} Dev UI loaded — open this page in the browser, then DevTools → Console. ` +
      'Vite’s terminal only shows the server; `[OwlbertWeb]` lines are emitted in the browser.',
  )
}
